/* ============================================================================
   LEAGUE LIDS — WELCOME POPUP · assets/welcome-popup.js
   Dual-mode (email_only drop-access / discount 10%-first-order). Zero deps.

   Primary capture = the ops-hub API (reliable; fires the email platform's
   consent event + Newsletter list join → live Welcome flow). A Shopify-native
   /contact post fires as a BEST-EFFORT secondary consent record only — never
   relied on alone (Shopify bot-challenges those posts and silently eats
   signups). ⛔ #82.

   Suppression: signup → never again ('llwp:done'); close → snooze N days
   ('llwp:snoozed'); logged-in customers never see it (section-level liquid);
   suppressed paths below; honeypot for bots. Trigger: delay OR scroll-depth
   OR desktop exit-intent, whichever first. Force-preview: ?wp_preview=1.
   ============================================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-welcome-popup]');
  if (!root || root.__llwpInit) return;
  root.__llwpInit = true;

  var MODE = root.getAttribute('data-mode') || 'email_only';
  var APP_BASE = (root.getAttribute('data-app-base') || 'https://app.leaguelids.com').replace(/\/+$/, '');
  var DELAY_MS = Math.max(2, parseInt(root.getAttribute('data-delay') || '6', 10)) * 1000;
  var SCROLL_PCT = Math.max(5, parseInt(root.getAttribute('data-scroll-percent') || '35', 10));
  var SNOOZE_DAYS = Math.max(1, parseInt(root.getAttribute('data-snooze-days') || '15', 10));
  var EXIT_INTENT = root.getAttribute('data-exit-intent') !== 'false';

  var KEY_DONE = 'llwp:done';
  var KEY_SNOOZED = 'llwp:snoozed';
  var SUPPRESSED_PATHS = /^\/(checkout|cart|account|challenge|policies|password)/;

  // ── buy-decision guards (2026-07-24) ──────────────────────────────────────
  // The popup fired ~6s into a PRODUCT page and covered the buy area on mobile.
  // That is the highest-intent moment on the site, and since the sticky
  // Add-to-cart bar shipped there is now ALWAYS a buy button on screen for the
  // popup to cover. So: never interrupt a live buy decision, but still capture
  // people who are browsing rather than buying. Rules approved by Ben 2026-07-24.
  var PAGE_TYPE = root.getAttribute('data-page-type') || '';
  var IS_PRODUCT = PAGE_TYPE === 'product';
  var PDP_MIN_DWELL_MS = 20000; // must have lingered past the buy box, not just scrolled by
  var CART_COOLDOWN_MS = 60000; // quiet window after any cart activity

  var cartCount = parseInt(root.getAttribute('data-cart-count') || '0', 10) || 0;
  var lastCartActivityAt = 0;
  var pageOpenedAt = Date.now();

  // The theme fires cart:change on every add/remove. Keep the seeded Liquid count
  // fresh, and treat any cart movement as "this person is buying right now".
  document.addEventListener('cart:change', function (event) {
    try {
      var c = event && event.detail && event.detail.cart;
      if (c && typeof c.item_count === 'number') cartCount = c.item_count;
      else cartCount = Math.max(cartCount, 1);
    } catch (e) {
      cartCount = Math.max(cartCount, 1);
    }
    lastCartActivityAt = Date.now();
  });

  // Any theme drawer / dialog that is actually on screen. ⛔ Do NOT test for an
  // `open` attribute or aria-modal: this theme sets aria-modal on every drawer
  // permanently and never sets `open`. Visibility is the real signal. offsetParent
  // is useless too — the drawers are position:fixed, so it is null even when open.
  function overlayOpen() {
    var els = document.querySelectorAll('cart-drawer, search-drawer, navigation-drawer, x-drawer, dialog[open], [role="dialog"]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el === root || root.contains(el) || el.contains(root)) continue; // ignore ourselves
      var cs = window.getComputedStyle(el);
      if (cs.visibility === 'hidden' || cs.display === 'none' || cs.opacity === '0') continue;
      var r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return true;
    }
    return false;
  }

  // On a product page, the buy decision is "live" until the real Add-to-cart has
  // scrolled out of view. Re-queried every time: the theme re-renders the buy box
  // inside <product-rerender> on every variant change, so a cached node goes stale.
  function buyDecisionLive() {
    if (!IS_PRODUCT) return false;
    var form = document.querySelector('form[is="product-form"]');
    // No buy form (sold out / coming soon) => nothing to interrupt.
    if (!form) return false;
    var atc = form.querySelector('buy-buttons:not(.ll-sb__cta)');
    if (!atc) return false;
    if (atc.getBoundingClientRect().bottom >= 0) return true;      // still on screen or below
    return Date.now() - pageOpenedAt < PDP_MIN_DWELL_MS;           // scrolled past, but only just
  }

  function canShow() {
    if (cartCount > 0) return false;                                   // mid-purchase
    if (Date.now() - lastCartActivityAt < CART_COOLDOWN_MS) return false; // just added something
    if (overlayOpen()) return false;                                   // don't stack overlays
    if (buyDecisionLive()) return false;                               // don't interrupt the buy
    return true;
  }

  var dialog = root.querySelector('.wp-dialog');
  var stepForm = root.querySelector('[data-wp-step="form"]');
  var stepSuccess = root.querySelector('[data-wp-step="success"]');
  var form = root.querySelector('[data-wp-form]');
  var emailInput = form ? form.querySelector('input[name="email"]') : null;
  var errorEl = root.querySelector('[data-wp-error]');
  var submitBtn = root.querySelector('[data-wp-submit]');

  function store(fn, fallback) { try { return fn(); } catch (e) { return fallback; } }
  function isDone() { return store(function () { return localStorage.getItem(KEY_DONE) === '1'; }, false); }
  function isSnoozed() {
    return store(function () {
      var until = parseInt(localStorage.getItem(KEY_SNOOZED) || '0', 10);
      return until > Date.now();
    }, false);
  }
  function markDone() { store(function () { localStorage.setItem(KEY_DONE, '1'); }); }
  function snooze() { store(function () { localStorage.setItem(KEY_SNOOZED, String(Date.now() + SNOOZE_DAYS * 864e5)); }); }

  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var forcePreview = /[?&]wp_preview=1/.test(location.search);

  // ── open / close ──────────────────────────────────────────────────────────
  var isOpen = false;
  var lastFocused = null;
  var prevOverflow = '';

  function focusables() {
    return dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    if (e.key !== 'Tab') return;
    var items = Array.prototype.filter.call(focusables(), function (el) { return el.offsetParent !== null; });
    if (items.length === 0) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    lastFocused = document.activeElement;
    root.hidden = false;
    prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Double rAF so the transition runs from the hidden state.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { root.setAttribute('data-open', ''); });
    });
    document.addEventListener('keydown', onKeydown, true);
    setTimeout(function () { if (emailInput && !stepForm.hidden) emailInput.focus({ preventScroll: true }); }, 380);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.removeAttribute('data-open');
    document.removeEventListener('keydown', onKeydown, true);
    document.body.style.overflow = prevOverflow;
    setTimeout(function () { root.hidden = true; }, 330);
    if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
    if (!isDone() && !designMode) snooze();
  }

  root.querySelectorAll('[data-wp-close]').forEach(function (el) { el.addEventListener('click', close); });
  var overlay = root.querySelector('[data-wp-overlay]');
  if (overlay) overlay.addEventListener('click', close);

  // ── triggers ──────────────────────────────────────────────────────────────
  var triggered = false;
  var gateTimer = null;
  var gateStartedAt = 0;
  var GATE_MAX_MS = 900000; // stop re-checking after 15 min; the visit is over

  // A trigger no longer opens the popup directly — it ARMS it. The popup then
  // opens at the first moment canShow() allows. Without this, a trigger that
  // fired during a live buy decision was simply lost (the old code set
  // `triggered` and tore the listeners down), so a shopper who scrolled past
  // the buy box later would never be asked at all.
  function trigger(bypassGate) {
    if (triggered || isOpen) return;
    triggered = true;
    cleanupTriggers();
    if (bypassGate || canShow()) { open(); return; }
    gateStartedAt = Date.now();
    gateTimer = setInterval(function () {
      if (isOpen) { clearInterval(gateTimer); gateTimer = null; return; }
      if (Date.now() - gateStartedAt > GATE_MAX_MS) { clearInterval(gateTimer); gateTimer = null; return; }
      if (canShow()) { clearInterval(gateTimer); gateTimer = null; open(); }
    }, 750);
  }

  var delayTimer = null;
  function onScroll() {
    var doc = document.documentElement;
    var max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    if ((window.scrollY / max) * 100 >= SCROLL_PCT) trigger();
  }
  var dwellStart = Date.now();
  function onMouseOut(e) {
    if (Date.now() - dwellStart < 3000) return;
    if (e.clientY <= 0 && !e.relatedTarget) trigger();
  }
  function cleanupTriggers() {
    if (delayTimer) clearTimeout(delayTimer);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onMouseOut);
  }

  function arm() {
    delayTimer = setTimeout(trigger, DELAY_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
    if (EXIT_INTENT && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', onMouseOut);
    }
  }

  // Theme editor: never auto-trigger; open/close with section selection.
  if (designMode) {
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target && e.target.contains(root)) open();
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.contains(root)) close();
    });
  } else if (forcePreview) {
    // ?wp_preview=1 must ALWAYS show, gates included — it is how the popup is
    // reviewed on a product page without waiting out the buy-decision rules.
    setTimeout(function () { trigger(true); }, 400);
  } else if (!isDone() && !isSnoozed() && !SUPPRESSED_PATHS.test(location.pathname)) {
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
  }

  // ── submit ────────────────────────────────────────────────────────────────
  function showError(msg, suggestion) {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.appendChild(document.createTextNode(msg + ' '));
    if (suggestion) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'Did you mean ' + suggestion + '?';
      btn.addEventListener('click', function () {
        emailInput.value = suggestion;
        clearError();
        emailInput.focus();
      });
      errorEl.appendChild(btn);
    }
    errorEl.setAttribute('data-show', '');
    if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
  }
  function clearError() {
    if (!errorEl) return;
    errorEl.removeAttribute('data-show');
    errorEl.textContent = '';
    if (emailInput) emailInput.removeAttribute('aria-invalid');
  }
  if (emailInput) emailInput.addEventListener('input', clearError);

  // Best-effort Shopify-native consent record. Fire-and-forget; NEVER the
  // primary path (bot challenges silently eat these). ⛔ #82.
  function fireNativeConsent(email) {
    try {
      var fd = new FormData();
      fd.append('form_type', 'customer');
      fd.append('utf8', '✓');
      fd.append('contact[email]', email);
      fd.append('contact[tags]', 'newsletter,welcome-popup');
      fetch('/contact', { method: 'POST', body: fd, credentials: 'same-origin' }).catch(function () {});
    } catch (e) { /* best-effort only */ }
  }

  function showSuccess(payload) {
    markDone();
    stepForm.hidden = true;
    stepSuccess.hidden = false;
    if (MODE === 'discount' && payload) {
      var codeEl = root.querySelector('[data-wp-code]');
      var applyEl = root.querySelector('[data-wp-apply]');
      var emailedEl = root.querySelector('[data-wp-emailed]');
      if (codeEl && payload.code) codeEl.textContent = payload.code;
      if (applyEl) {
        applyEl.href = payload.code
          ? '/discount/' + encodeURIComponent(payload.code) + '?redirect=%2Fcollections%2Fall'
          : '/collections/all';
      }
      if (emailedEl && payload.emailed) emailedEl.hidden = false;
      store(function () { if (payload.code) localStorage.setItem('llwp:code', payload.code); });
    }
    var firstBtn = stepSuccess.querySelector('button, a');
    if (firstBtn) setTimeout(function () { firstBtn.focus({ preventScroll: true }); }, 120);
  }

  // Tap-to-copy on the code plate (discount mode).
  var plate = root.querySelector('[data-wp-copy]');
  if (plate) {
    plate.addEventListener('click', function () {
      var code = (root.querySelector('[data-wp-code]') || {}).textContent || '';
      if (!code || code === '—') return;
      var hint = root.querySelector('[data-wp-copy-hint]');
      function copied() {
        plate.setAttribute('data-copied', '');
        if (hint) hint.textContent = 'Copied ✓';
        setTimeout(function () {
          plate.removeAttribute('data-copied');
          if (hint) hint.textContent = 'Tap to copy';
        }, 2200);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(copied).catch(function () {});
      } else {
        var sel = window.getSelection();
        var range = document.createRange();
        range.selectNodeContents(root.querySelector('[data-wp-code]'));
        sel.removeAllRanges();
        sel.addRange(range);
        try { document.execCommand('copy'); copied(); } catch (e) { /* noop */ }
      }
    });
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearError();
      var email = (emailInput.value || '').trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        showError('Please enter a valid email address.');
        emailInput.focus();
        return;
      }

      var honeypot = form.querySelector('input[name="website"]');
      var endpoint = MODE === 'discount'
        ? APP_BASE + '/api/email/popup-offer'
        : APP_BASE + '/api/email/contacts/subscribe';
      var body = MODE === 'discount'
        ? { email: email, website: honeypot ? honeypot.value : '' }
        : { email: email, source_tag: 'popup-drop-access', website: honeypot ? honeypot.value : '' };

      submitBtn.disabled = true;

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (res) {
          return res.json().then(function (json) { return { ok: res.ok, json: json }; }).catch(function () {
            return { ok: res.ok, json: {} };
          });
        })
        .then(function (r) {
          submitBtn.disabled = false;
          if (!r.ok) {
            showError(r.json.error || 'Something went wrong. Please try again.', r.json.suggestion || null);
            return;
          }
          fireNativeConsent(email);
          showSuccess(r.json);
        })
        .catch(function () {
          submitBtn.disabled = false;
          showError('Connection hiccup. Please try again.');
        });
    });
  }
})();
